import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Rainbow, PawPrint, Waves, Key, Star, MapPin, Sun, Boat, Airplane, Sparkle, House, ArrowUpRight, Heart,
} from "@phosphor-icons/react";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd, faqJsonLd } from "@/lib/schema";
import { properties } from "@/data/properties";
import { BookingScores } from "@/components/site/BookingScores";
import {
  aboutIntro, aboutFacts, aboutSections, aboutProximity, aboutFaqs, hosts,
} from "@/data/about";

const ICONS = { Rainbow, PawPrint, Waves, Key, Star, MapPin, Sun, Boat, Airplane };
const Icon = ({ name, ...props }) => {
  const C = ICONS[name] || Sparkle;
  return <C {...props} />;
};

// Rich, aggregateRating-free business node — the canonical entity definition.
// (Rating is carried by the sitewide #business node; we don't duplicate it here.)
const businessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "@id": `${SITE}/#business`,
  name: "Double Rainbow Suites",
  alternateName: "Double Rainbow Suites — Boutique Vacation Rentals",
  url: SITE,
  slogan: "Where every stay ends in a rainbow.",
  description: aboutIntro,
  telephone: "+1-954-332-5725",
  priceRange: "$$",
  image: `${SITE}/logo.png`,
  address: {
    "@type": "PostalAddress",
    streetAddress: "1425 NE 24th St",
    addressLocality: "Wilton Manors",
    addressRegion: "FL",
    postalCode: "33305",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 26.1489, longitude: -80.1436 },
  areaServed: [
    { "@type": "City", name: "Wilton Manors" },
    { "@type": "City", name: "Fort Lauderdale" },
  ],
  knowsAbout: [
    "LGBTQ+ travel", "Wilton Drive", "Wilton Manors vacation rentals",
    "pet-friendly vacation rentals", "private pool rentals", "Fort Lauderdale Beach",
  ],
  petsAllowed: true,
  amenityFeature: [
    "Free Wi-Fi", "Private pool (select homes)", "Full kitchen", "Self check-in",
    "Free parking", "Pet friendly", "Smart TV", "Workspace", "Beach-cruiser bikes",
  ].map((name) => ({ "@type": "LocationFeatureSpecification", name, value: true })),
  sameAs: [
    "https://instagram.com/doublerainbowsuites",
    "https://www.facebook.com/people/Double-Rainbow-Suites/100078176427437/",
    "https://youtube.com/@doublerainbowsuites",
  ],
  founder: [
    { "@type": "Person", name: "Bill", jobTitle: "Co-Founder", worksFor: { "@id": `${SITE}/#business` } },
    { "@type": "Person", name: "Jonathan", jobTitle: "Co-Founder & Interior Designer", knowsLanguage: ["English", "Mandarin Chinese", "Cantonese"], knowsAbout: ["Interior design", "Hospitality interior design"], worksFor: { "@id": `${SITE}/#business` } },
  ],
  award: "Airbnb Superhost — 3 consecutive years",
};

const aboutPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${SITE}/about#webpage`,
  url: `${SITE}/about`,
  name: "Why Double Rainbow Suites — Boutique Vacation Rentals in Wilton Manors & Fort Lauderdale",
  about: { "@id": `${SITE}/#business` },
  mainEntity: { "@id": `${SITE}/#business` },
};

const propertiesItemList = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Double Rainbow Suites properties",
  itemListElement: properties.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "LodgingBusiness",
      name: p.name,
      url: `${SITE}/properties/${p.slug}`,
    },
  })),
};

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Why Double Rainbow Suites — Boutique Vacation Rentals in Wilton Manors & Fort Lauderdale"
        description="Who we are, where we are and why guests choose us. Double Rainbow Suites is an LGBTQ+ owned, pet-friendly boutique vacation-rental company in Wilton Manors & Fort Lauderdale — king suites and a private-pool home, walkable to Wilton Drive, bookable direct."
        canonical={`${SITE}/about`}
        jsonLd={[
          businessJsonLd,
          aboutPageJsonLd,
          propertiesItemList,
          faqJsonLd(aboutFaqs),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Why Double Rainbow Suites", path: "/about" },
          ]),
        ]}
      />

      {/* Hero */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <motion.div {...fade()}>
          <p className="overline text-sunset mb-5 flex items-center gap-2">
            <Rainbow size={18} weight="fill" className="text-sunset" /> Why Double Rainbow Suites
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Boutique vacation rentals in Wilton Manors &amp; Fort Lauderdale.
          </h1>
          <p className="text-lg md:text-xl text-emerald-soft font-light leading-relaxed max-w-3xl mt-7">
            {aboutIntro}
          </p>
        </motion.div>

        {/* Trust badges */}
        <motion.ul
          {...fade(0.1)}
          data-testid="about-facts"
          className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5"
        >
          {aboutFacts.map((f, i) => (
            <li key={i} data-testid={`about-fact-${i}`} className="flex items-center gap-3">
              <span className="w-10 h-10 shrink-0 rounded-full bg-sunset/10 grid place-items-center">
                <Icon name={f.icon} size={18} weight="fill" className="text-sunset" />
              </span>
              <span className="text-emerald text-sm md:text-base">{f.label}</span>
            </li>
          ))}
        </motion.ul>
        <BookingScores className="mt-14" />
      </div>

      {/* Narrative sections */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-14 md:gap-y-20">
          {aboutSections.map((s, i) => (
            <motion.section key={s.id} id={s.id} {...fade((i % 2) * 0.06)} className="scroll-mt-32" data-testid={`about-section-${s.id}`}>
              <h2 className="font-display text-2xl md:text-3xl leading-snug tracking-tight border-t border-emerald/15 pt-6">
                {s.h}
              </h2>
              {s.body.map((p, j) => (
                <p key={j} className="text-emerald-soft font-light leading-relaxed mt-4">
                  {p}
                </p>
              ))}
            </motion.section>
          ))}
        </div>
      </div>

      {/* Meet your hosts */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <motion.div
          {...fade()}
          data-testid="meet-your-hosts"
          className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-10 md:gap-14 items-center rounded-3xl border border-emerald/12 bg-sand-2 p-8 md:p-12"
        >
          <div className="mx-auto md:mx-0">
            {hosts.photo ? (
              <img
                src={hosts.photo}
                alt={`${hosts.names} — hosts of Double Rainbow Suites`}
                data-testid="hosts-photo"
                width={226}
                height={195}
                className="w-56 h-56 md:w-64 md:h-64 object-contain mx-auto [filter:drop-shadow(0_12px_22px_rgba(15,61,46,0.18))]"
              />
            ) : (
              <div
                data-testid="hosts-monogram"
                aria-hidden="true"
                className="w-52 h-52 md:w-72 md:h-72 rounded-full grid place-items-center bg-gradient-to-br from-sunset via-gold to-sage shadow-lg"
              >
                <span className="font-display text-6xl md:text-7xl text-sand tracking-tight">{hosts.initials}</span>
              </div>
            )}
          </div>
          <div>
            <p className="overline text-sunset mb-4 flex items-center gap-2">
              <Heart size={16} weight="fill" /> Meet your hosts
            </p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">
              Hosted by {hosts.names}.
            </h2>
            <p className="text-emerald-soft font-light leading-relaxed mt-5 max-w-2xl">
              {hosts.blurb}
            </p>
            <ul data-testid="hosts-credentials" className="flex flex-wrap gap-2 mt-6">
              {hosts.credentials.map((c, i) => (
                <li
                  key={i}
                  className="inline-flex items-center rounded-full border border-emerald/15 bg-sand px-3.5 py-1.5 text-xs md:text-sm text-emerald"
                >
                  {c}
                </li>
              ))}
            </ul>
            <p className="font-display text-xl text-emerald mt-6">{hosts.signature}</p>
            <p data-testid="hosts-platforms" className="text-emerald-soft text-xs md:text-sm mt-4 tracking-wide">
              Also reviewed &amp; featured on {hosts.platforms.join(" · ")}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Our properties */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <motion.div {...fade()} className="mb-10">
          <p className="overline text-sunset mb-4 flex items-center gap-2">
            <House size={16} weight="fill" /> Our properties
          </p>
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight">
            Four homes, one warm welcome.
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5">
          {properties.map((p, i) => (
            <motion.div key={p.slug} {...fade((i % 2) * 0.06)}>
              <Link
                to={`/properties/${p.slug}`}
                data-testid={`about-property-${p.slug}`}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-emerald/12 bg-sand-2 p-6 transition-colors duration-300 hover:border-sunset/40"
              >
                <div>
                  <p className="font-display text-xl md:text-2xl leading-snug text-emerald">{p.shortName}</p>
                  <p className="text-emerald-soft text-sm mt-1.5">
                    {p.location} · sleeps {p.guests} · {p.bedrooms} bd · {p.bathrooms} ba
                  </p>
                </div>
                <ArrowUpRight size={22} className="shrink-0 text-sunset transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Proximity */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <motion.div {...fade()} className="mb-10">
          <p className="overline text-sunset mb-4 flex items-center gap-2">
            <MapPin size={16} weight="fill" /> Your location
          </p>
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight">
            Central to everything that matters.
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5" data-testid="about-proximity">
          {aboutProximity.map((p, i) => (
            <motion.div
              key={i}
              {...fade((i % 2) * 0.06)}
              data-testid={`about-proximity-${i}`}
              className="rounded-2xl border border-emerald/12 p-7 bg-sand-2"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-11 h-11 rounded-full bg-sunset/10 grid place-items-center">
                  <Icon name={p.icon} size={20} weight="fill" className="text-sunset" />
                </span>
                <h3 className="font-display text-xl md:text-2xl text-emerald leading-tight">{p.place}</h3>
              </div>
              <p className="text-emerald font-light">{p.detail}</p>
              <p className="text-emerald-soft text-sm mt-2">{p.note}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <motion.div {...fade()} className="mb-10">
          <p className="overline text-sunset mb-4 flex items-center gap-2">
            <Sparkle size={16} weight="fill" /> In short
          </p>
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight">
            The essentials, answered.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
          {aboutFaqs.map((f, i) => (
            <motion.div key={i} {...fade((i % 2) * 0.06)} className="border-t border-emerald/15 pt-6" data-testid={`about-faq-${i}`}>
              <h3 className="font-display text-xl md:text-2xl leading-snug text-emerald mb-3">{f.q}</h3>
              <p className="text-emerald-soft font-light leading-relaxed">{f.a}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <motion.div
          {...fade()}
          className="rounded-3xl bg-emerald text-sand p-10 md:p-16 text-center"
          data-testid="about-cta"
        >
          <h2 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight max-w-2xl mx-auto">
            Come stay where every trip ends in a rainbow.
          </h2>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/properties"
              data-testid="about-cta-book"
              className="group inline-flex items-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-gold hover:text-emerald"
            >
              Book a stay
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-sand/25 px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-sand/10"
            >
              Ask us anything
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
